import { strings } from './strings';

// Ticket-office working-hours data comes from InfoPub, a Portuguese-only legacy
// data source with no localization support (and none planned - it's being
// replaced by a new version). When the user is viewing the site in English we do
// an ad-hoc, case-preserving replacement of a small set of well-known day-name /
// connector terms. This is intentionally simple: it is a best-effort cosmetic
// translation for a legacy data source, not a full i18n layer.

// The Portuguese -> English term map is served statically from
// public/docs/working-hours-translations.json so it can be edited without
// rebuilding the site. It is a flat object of canonical Portuguese term
// (lowercased) -> canonical English term (lowercased); the casing of each match
// is re-applied at replacement time (see applyCasing). Until the file is fetched
// the map is empty and translateWorkingHoursText returns its input untouched.
const TRANSLATIONS_URL = `${process.env.PUBLIC_URL}/docs/working-hours-translations.json`;

let translationMap = {};
let matcher = null;
let loadPromise = null;

// Letters that count as "word characters" for boundary detection, including the
// accented characters used in Portuguese. Default \b / \w do not handle accents.
const letters = 'A-Za-zÀ-ÿ';
const boundary = `[^${letters}]`;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// (Re)build the matcher from the current translationMap. Longest terms first so
// e.g. "segunda-feira" wins over "segunda".
const buildMatcher = () => {
    const sortedTerms = Object.keys(translationMap).sort((a, b) => b.length - a.length);

    if (0 === sortedTerms.length) {
        matcher = null;
        return;
    }

    const alternation = sortedTerms.map(escapeRegExp).join('|');

    // Capture a leading boundary (or start of string) so adjacent words don't bleed
    // into each other, and require a trailing boundary (or end of string).
    matcher = new RegExp(`(^|${boundary})(${alternation})(?=${boundary}|$)`, 'gi');
};

/**
 * Fetches the static translation map and rebuilds the matcher. The fetch happens
 * at most once per page load (the promise is cached); callers can await it and
 * re-render so already-rendered Portuguese text gets translated once loaded.
 */
export const loadWorkingHoursTranslations = () => {
    if (null === loadPromise) {
        loadPromise = fetch(TRANSLATIONS_URL)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Unexpected response ${response.status} for ${TRANSLATIONS_URL}`);
                }
                return response.json();
            })
            .then(data => {
                translationMap = data || {};
                buildMatcher();
            })
            .catch(error => {
                // Best-effort: on failure leave the map empty so text is returned
                // untouched rather than breaking the ticket office view.
                console.error('Error loading working hours translations:', error);
            });
    }

    return loadPromise;
};

const isUpperCase = (value) => value === value.toUpperCase() && value !== value.toLowerCase();

// Capitalize only the first letter of the translated term, not every word — most
// entries here are full phrases (e.g. "sem horário definido"), and title-casing
// them all would read as "No Fixed Schedule" instead of "No fixed schedule".
const capitalizeFirst = (value) =>
    value.replace(new RegExp(`[${letters}]`), (char) => char.toUpperCase());

// Re-apply the casing style of the original Portuguese match to the English term.
const applyCasing = (translated, original) => {
    if (isUpperCase(original)) {
        return translated.toUpperCase();
    }

    const firstLetter = original.match(new RegExp(`[${letters}]`));
    if (firstLetter && firstLetter[0] === firstLetter[0].toUpperCase()) {
        return capitalizeFirst(translated);
    }

    return translated;
};

/**
 * Translates known Portuguese working-hours terms to English when the active
 * language is English. Any other language (or unknown text) is returned
 * untouched.
 */
export const translateWorkingHoursText = (text) => {
    if (!text || 'en' !== strings.locale || null === matcher) {
        return text;
    }

    return text.replace(matcher, (match, pre, term) => {
        const translated = translationMap[term.toLowerCase()];
        return pre + applyCasing(translated, term);
    });
};
